/**
 * Tenant Management Section
 * @description 테넌트 관리 - 테넌트 CRUD 기능
 */

import { useState, useEffect, useMemo, type ReactNode } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ScrollArea,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@shared/ui/primitives';
import {
  Building2,
  Copy,
  Check,
  Eye,
  EyeOff,
  Mail,
  Pencil,
  Plus,
  Settings2,
  Trash2,
  AlertTriangle,
  UserCog,
} from 'lucide-react';
import { apiClient } from '@/utils/httpClient';

interface Tenant {
  tenantId: string;
  tenantName: string;
  status: 'active' | 'inactive';
  metadata?: {
    description?: string;
    contact?: {
      email?: string;
      phone?: string;
    };
    portalLogo?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export const TenantManagementSection: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingTenantId, setEditingTenantId] = useState<string | null>(null);
  const [deletingTenantId, setDeletingTenantId] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    tenantId: string;
    tenantName: string;
    useTemplate: boolean;
    tenantAdminPassword: string;
    metadata: {
      description: string;
      contact: {
        email: string;
        phone: string;
      };
      portalLogo: string;
    };
  }>({
    tenantId: '',
    tenantName: '',
    useTemplate: true,
    tenantAdminPassword: '',
    metadata: {
      description: '',
      contact: {
        email: '',
        phone: '',
      },
      portalLogo: '',
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [adminIdCopied, setAdminIdCopied] = useState(false);

  const editingTenant = useMemo(
    () => (editingTenantId ? tenants.find((t) => t.tenantId === editingTenantId) : null),
    [editingTenantId, tenants],
  );

  const canonicalAdminId = editingTenantId ? `${editingTenantId}-admin` : '';

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setIsLoading(true);
      const data = await apiClient.get<{ tenants: Tenant[]; totalCount: number }>(
        '/admin/tenants'
      );
      setTenants(data.tenants || []);
      setError(null);
    } catch (error) {
      console.error('Error loading tenants:', error);
      setError('Failed to load tenant list.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTenantId(null);
    setFormData({
      tenantId: '',
      tenantName: '',
      useTemplate: true,
      tenantAdminPassword: '',
      metadata: {
        description: '',
        contact: {
          email: '',
          phone: '',
        },
        portalLogo: '',
      },
    });
    setDialogError(null);
    setShowAdminPassword(false);
    setAdminIdCopied(false);
    setError(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (tenant: Tenant) => {
    setEditingTenantId(tenant.tenantId);
    setFormData({
      tenantId: tenant.tenantId,
      tenantName: tenant.tenantName,
      useTemplate: false, // 편집 시에는 템플릿 사용 안 함
      tenantAdminPassword: '',
      metadata: {
        description: tenant.metadata?.description || '',
        contact: {
          email: tenant.metadata?.contact?.email || '',
          phone: tenant.metadata?.contact?.phone || '',
        },
        portalLogo: tenant.metadata?.portalLogo || '',
      },
    });
    setDialogError(null);
    setShowAdminPassword(false);
    setAdminIdCopied(false);
    setError(null);
    setIsDialogOpen(true);
  };

  const copyAdminId = async () => {
    if (!canonicalAdminId) return;
    try {
      await navigator.clipboard.writeText(canonicalAdminId);
      setAdminIdCopied(true);
      window.setTimeout(() => setAdminIdCopied(false), 2000);
    } catch {
      setDialogError('관리자 ID 복사에 실패했습니다.');
    }
  };

  const handleDelete = (tenantId: string) => {
    setDeletingTenantId(tenantId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingTenantId) return;

    try {
      setIsLoading(true);
      setError(null);
      
      await apiClient.delete(`/admin/tenants/${encodeURIComponent(deletingTenantId)}`);
      
      // 성공적으로 삭제된 경우
      await loadTenants();
      setIsDeleteDialogOpen(false);
      setDeletingTenantId(null);
      setError(null);
      
      // 성공 메시지는 다이얼로그가 닫힌 후 표시할 수 있도록 상태 관리
      // (선택사항: toast notification 등 사용 가능)
    } catch (error: any) {
      console.error('Error deleting tenant:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.status,
        response: error?.response,
      });
      
      // API 에러 응답에서 메시지 추출
      let errorMessage = 'Failed to delete tenant';
      
      // httpClient에서 반환하는 에러는 이미 파싱된 메시지를 포함
      if (error?.response?.message) {
        errorMessage = error.response.message;
      } else if (error?.response?.error?.message) {
        errorMessage = error.response.error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      // 상태 코드에 따른 추가 정보 제공
      const status = error?.status;
      if (status === 400) {
        errorMessage = `Bad Request: ${errorMessage}`;
      } else if (status === 403) {
        errorMessage = `Permission Denied: ${errorMessage}. You need super_admin permission to delete tenants.`;
      } else if (status === 404) {
        errorMessage = `Not Found: ${errorMessage}. The tenant may have already been deleted.`;
      } else if (status === 500) {
        errorMessage = `Server Error: ${errorMessage}. Please check the server logs.`;
      }
      
      setError(errorMessage);
      
      // 다이얼로그는 열어두어 에러 메시지를 볼 수 있도록 함
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.tenantId.trim()) {
      setDialogError('테넌트 ID를 입력해 주세요.');
      return;
    }
    if (!formData.tenantName.trim()) {
      setDialogError('테넌트 이름을 입력해 주세요.');
      return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(formData.tenantId)) {
      setDialogError('테넌트 ID는 영문, 숫자, 하이픈(-), 밑줄(_)만 사용할 수 있습니다.');
      return;
    }

    try {
      setIsLoading(true);
      setDialogError(null);
      setError(null);

      if (editingTenantId) {
        // 수정
        await apiClient.patch(`/admin/tenants/${editingTenantId}`, {
          tenantName: formData.tenantName,
          metadata: formData.metadata,
        });

        // tenant 관리자 계정은 단일({tenantId}-admin)만 유지하며
        // 비밀번호는 입력된 경우에만 변경 (빈 값이면 정합성만 강제)
        const trimmedPw = formData.tenantAdminPassword.trim();
        await apiClient.post(
          `/admin/tenants/${editingTenantId}/tenant-admin/reset-password`,
          trimmedPw ? { newPassword: trimmedPw } : {},
        );
      } else {
        await apiClient.post('/admin/tenants', {
          tenantId: formData.tenantId,
          tenantName: formData.tenantName,
          useTemplate: formData.useTemplate,
          metadata: formData.metadata,
        });
      }

      await loadTenants();
      setIsDialogOpen(false);
      setEditingTenantId(null);
      setShowAdminPassword(false);
    } catch (error: unknown) {
      console.error('Error saving tenant:', error);
      const errorMessage =
        error instanceof Error ? error.message : '테넌트 저장에 실패했습니다.';
      setDialogError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge variant="default" className="bg-green-500">Active</Badge>
    ) : (
      <Badge variant="secondary">Inactive</Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Tenant Management</h2>
          <p className="text-muted-foreground mt-1">
            Create, edit, and delete tenants.
          </p>
        </div>
        <Button onClick={handleAdd} disabled={isLoading}>
          <Plus className="w-4 h-4 mr-2" />
          Add Tenant
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tenant ID</TableHead>
              <TableHead>Tenant Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No tenants found.
                </TableCell>
              </TableRow>
            ) : (
              tenants.map((tenant) => (
                <TableRow key={tenant.tenantId}>
                  <TableCell className="font-medium">{tenant.tenantId}</TableCell>
                  <TableCell>{tenant.tenantName}</TableCell>
                  <TableCell>{getStatusBadge(tenant.status)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {tenant.metadata?.description || '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {tenant.createdAt
                      ? new Date(tenant.createdAt).toLocaleDateString('ko-KR')
                      : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(tenant)}
                        disabled={isLoading}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(tenant.tenantId)}
                        disabled={isLoading}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* 생성/수정 다이얼로그 */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setDialogError(null);
            setShowAdminPassword(false);
            setAdminIdCopied(false);
          }
        }}
      >
        <DialogContent className="flex max-h-[min(90vh,820px)] max-w-2xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="space-y-3 border-b px-6 py-5 text-left">
            <div className="flex items-start gap-3 pr-8">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                {editingTenantId ? (
                  <Pencil className="h-5 w-5" />
                ) : (
                  <Plus className="h-5 w-5" />
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <DialogTitle className="text-xl">
                  {editingTenantId ? '테넌트 수정' : '테넌트 추가'}
                </DialogTitle>
                <DialogDescription className="text-sm leading-relaxed">
                  {editingTenantId
                    ? '기본 정보·관리자 계정·연락처를 수정합니다. 저장 시 관리자 계정 정합성이 자동으로 맞춰집니다.'
                    : '새 테넌트를 생성합니다. 템플릿을 사용하면 기본 설정이 자동으로 생성됩니다.'}
                </DialogDescription>
              </div>
            </div>

            {editingTenantId && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2.5">
                <Badge variant="outline" className="font-mono text-xs">
                  {formData.tenantId}
                </Badge>
                <span className="text-sm font-medium text-foreground">
                  {formData.tenantName || editingTenant?.tenantName}
                </span>
                {editingTenant && getStatusBadge(editingTenant.status)}
              </div>
            )}
          </DialogHeader>

          <ScrollArea className="flex-1 px-6 py-5" orientation="vertical">
            <div className="space-y-6 pr-2">
              {dialogError && (
                <Alert
                  variant="error"
                  title="저장할 수 없습니다"
                  description={dialogError}
                />
              )}

              <FormSection
                title="기본 정보"
                description="테넌트 식별자와 표시 이름"
                icon={<Building2 className="h-4 w-4" />}
              >
                {!editingTenantId ? (
                  <div className="space-y-2">
                    <Label htmlFor="tenantId">테넌트 ID *</Label>
                    <Input
                      id="tenantId"
                      value={formData.tenantId}
                      onChange={(e) =>
                        setFormData({ ...formData, tenantId: e.target.value })
                      }
                      placeholder="예: newtenant"
                      className="font-mono"
                    />
                    <p className="text-xs text-muted-foreground">
                      영문, 숫자, 하이픈(-), 밑줄(_)만 사용할 수 있습니다.
                    </p>
                  </div>
                ) : null}

                <div className="space-y-2">
                  <Label htmlFor="tenantName">테넌트 이름 *</Label>
                  <Input
                    id="tenantName"
                    value={formData.tenantName}
                    onChange={(e) =>
                      setFormData({ ...formData, tenantName: e.target.value })
                    }
                    placeholder="예: SK AX"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">설명</Label>
                  <Textarea
                    id="description"
                    value={formData.metadata.description}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        metadata: {
                          ...formData.metadata,
                          description: e.target.value,
                        },
                      })
                    }
                    placeholder="테넌트에 대한 간단한 설명"
                    rows={3}
                    className="resize-none"
                  />
                </div>

                {!editingTenantId && (
                  <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
                    <Checkbox
                      id="useTemplate"
                      checked={formData.useTemplate}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, useTemplate: checked })
                      }
                    />
                    <div className="space-y-0.5">
                      <Label htmlFor="useTemplate" className="cursor-pointer font-medium">
                        템플릿 사용
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        org, credentials, access-control 등 기본 설정을 자동 생성합니다.
                      </p>
                    </div>
                  </div>
                )}
              </FormSection>

              {editingTenantId && (
                <>
                  <Separator />
                  <FormSection
                    title="테넌트 관리자"
                    description="테넌트당 1명 · ID 고정 · tenant_admin 권한"
                    icon={<UserCog className="h-4 w-4" />}
                  >
                    <div className="space-y-2">
                      <Label>관리자 ID</Label>
                      <div className="flex gap-2">
                        <div className="flex min-h-10 flex-1 items-center rounded-md border bg-muted/50 px-3">
                          <code className="truncate font-mono text-sm text-foreground">
                            {canonicalAdminId}
                          </code>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="shrink-0"
                          onClick={copyAdminId}
                          title="관리자 ID 복사"
                          aria-label="관리자 ID 복사"
                        >
                          {adminIdCopied ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        형식 <span className="font-mono">{'{tenantId}'}-admin</span>으로 고정되며 변경할 수 없습니다.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tenantAdminPassword">관리자 비밀번호</Label>
                      <div className="relative">
                        <Input
                          id="tenantAdminPassword"
                          type={showAdminPassword ? 'text' : 'password'}
                          value={formData.tenantAdminPassword}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              tenantAdminPassword: e.target.value,
                            })
                          }
                          placeholder="변경할 때만 입력"
                          className="pr-10"
                          autoComplete="new-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          onClick={() => setShowAdminPassword((v) => !v)}
                          aria-label={showAdminPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
                        >
                          {showAdminPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        비워 두면 비밀번호는 유지되고, 계정·권한 정합성만 복구됩니다.
                      </p>
                    </div>

                    <Alert
                      variant={formData.tenantAdminPassword.trim() ? 'warning' : 'neutral'}
                      title={
                        formData.tenantAdminPassword.trim()
                          ? '비밀번호 변경 시 주의'
                          : '저장 시 자동 처리'
                      }
                      description={
                        formData.tenantAdminPassword.trim()
                          ? `저장하면 기존 관리자 계정이 삭제되고 ${canonicalAdminId}만 유지되며, 비밀번호가 갱신됩니다.`
                          : `다른 관리자 계정이 있으면 제거되고 ${canonicalAdminId}만 tenant_admin 권한으로 유지됩니다.`
                      }
                    />
                  </FormSection>
                </>
              )}

              <Separator />

              <FormSection
                title="연락처 · 브랜딩"
                description="선택 입력 · 테넌트 포털 표시에 사용"
                icon={<Settings2 className="h-4 w-4" />}
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                      연락 이메일
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.metadata.contact.email}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          metadata: {
                            ...formData.metadata,
                            contact: {
                              ...formData.metadata.contact,
                              email: e.target.value,
                            },
                          },
                        })
                      }
                      placeholder="contact@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">연락 전화</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.metadata.contact.phone}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          metadata: {
                            ...formData.metadata,
                            contact: {
                              ...formData.metadata.contact,
                              phone: e.target.value,
                            },
                          },
                        })
                      }
                      placeholder="02-1234-5678"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="portalLogo">포털 로고 텍스트</Label>
                  <Input
                    id="portalLogo"
                    value={formData.metadata.portalLogo}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        metadata: {
                          ...formData.metadata,
                          portalLogo: e.target.value,
                        },
                      })
                    }
                    placeholder="예: My Company Portal"
                  />
                  <p className="text-xs text-muted-foreground">
                    테넌트 로그인 화면 좌측 상단에 표시됩니다.
                  </p>
                </div>
              </FormSection>
            </div>
          </ScrollArea>

          <DialogFooter className="gap-2 border-t bg-muted/20 px-6 py-4 sm:justify-between">
            <p className="hidden text-xs text-muted-foreground sm:block">
              {editingTenantId
                ? '필수: 테넌트 이름 · 관리자 정합성은 저장 시 자동 반영'
                : '필수: 테넌트 ID, 테넌트 이름'}
            </p>
            <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={isLoading}
                className="sm:min-w-[96px]"
              >
                취소
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="sm:min-w-[96px]"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                    저장 중…
                  </span>
                ) : (
                  '저장'
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={(open) => {
        if (!open && !isLoading) {
          setIsDeleteDialogOpen(false);
          setDeletingTenantId(null);
          setError(null);
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <DialogTitle className="text-xl font-semibold">
                Delete Tenant
              </DialogTitle>
            </div>
            <div className="text-base pt-2 space-y-4">
              <DialogDescription asChild>
                <p className="font-medium text-foreground mb-2">
                  Are you sure you want to delete tenant &quot;<span className="font-mono font-semibold">{deletingTenantId}</span>&quot;?
                </p>
              </DialogDescription>
              <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-800 dark:text-red-200 font-medium mb-2">
                  ⚠️ Warning: This action cannot be undone
                </p>
                <ul className="text-sm text-red-700 dark:text-red-300 space-y-1 list-disc list-inside">
                  <li>All tenant data will be permanently deleted</li>
                  <li>All configuration files will be removed</li>
                  <li>All user accounts will be deleted</li>
                  <li>All tickets and workflows will be lost</li>
                </ul>
              </div>
              
              {/* 에러 메시지 표시 */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg p-3">
                  <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                    Error: {error}
                  </p>
                </div>
              )}
            </div>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeletingTenantId(null);
                setError(null);
              }}
              disabled={isLoading}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={isLoading}
              className="min-w-[100px] bg-red-600 hover:bg-red-700 focus:ring-red-500"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Deleting...
                </span>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface FormSectionProps {
  title: string;
  description?: string;
  icon: ReactNode;
  children: ReactNode;
}

function FormSection({ title, description, icon, children }: FormSectionProps) {
  return (
    <section className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          {icon}
        </div>
        <div className="min-w-0 pt-0.5">
          <h3 className="text-sm font-semibold leading-none">{title}</h3>
          {description ? (
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      <div className="space-y-4 rounded-lg border border-border/60 bg-card/40 p-4">
        {children}
      </div>
    </section>
  );
}

TenantManagementSection.displayName = 'TenantManagementSection';
